import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge, Button, Input, Toggle } from '../../../components/ui/index.js';
import type {
  EvaluationCriterion,
  EvaluationTemplateItem,
  EvaluationTemplateRef,
} from '@confluent/shared/lib/evaluation/evaluation-template-contracts';

// Canonical spec shapes are the single source of truth in the shared package
// (the client resolves `@confluent/shared` like the rest of the app). Re-exported
// under the composer's local names so the consuming page/drawer are unaffected.
export type CriterionItem = EvaluationCriterion;
export type TemplateRefItem = EvaluationTemplateRef;
export type SpecItem = EvaluationTemplateItem;

interface SpecComposerProps {
  subjectKind: string;
  items: SpecItem[];
  onChange: (items: SpecItem[]) => void;
}

function newCriterion(): CriterionItem {
  return { kind: 'criterion', evaluatorKey: '', thresholds: {}, params: {}, minTrials: 0, required: true };
}

function newTemplateRef(): TemplateRefItem {
  return { kind: 'template_ref', childTemplateRef: '', childVersion: 1, required: true };
}

// ── Threshold row ─────────────────────────────────────────────────────────────

interface ThresholdRowProps {
  metricKey: string;
  value: number;
  onChangeKey: (newKey: string) => void;
  onChangeValue: (v: number) => void;
  onRemove: () => void;
}

const ThresholdRow: React.FC<ThresholdRowProps> = ({ metricKey, value, onChangeKey, onChangeValue, onRemove }) => (
  <div className="flex items-center gap-2 mt-1">
    <Input
      placeholder="metric key"
      value={metricKey}
      onChange={(e) => onChangeKey(e.target.value)}
      className="flex-1 text-xs py-1"
    />
    <Input
      type="number"
      placeholder="value"
      value={String(value)}
      onChange={(e) => onChangeValue(parseFloat(e.target.value) || 0)}
      className="w-24 text-xs py-1"
    />
    <Button variant="ghost" size="sm" iconOnly aria-label="Remove threshold" onClick={onRemove}>
      <Trash2 size={12} />
    </Button>
  </div>
);

// ── Criterion editor ──────────────────────────────────────────────────────────

interface CriterionEditorProps {
  item: CriterionItem;
  index: number;
  onChange: (updated: CriterionItem) => void;
  onRemove: () => void;
}

const CriterionEditor: React.FC<CriterionEditorProps> = ({ item, index, onChange, onRemove }) => {
  const [expanded, setExpanded] = useState(true);

  const setField = <K extends keyof CriterionItem>(key: K, val: CriterionItem[K]) => onChange({ ...item, [key]: val });

  const addThreshold = () => onChange({ ...item, thresholds: { ...item.thresholds, '': 0 } });

  const updateThresholdKey = (oldKey: string, newKey: string) => {
    const entries = Object.entries(item.thresholds).map(([k, v]) => (k === oldKey ? [newKey, v] : [k, v]));
    onChange({ ...item, thresholds: Object.fromEntries(entries) });
  };

  const updateThresholdValue = (key: string, val: number) =>
    onChange({ ...item, thresholds: { ...item.thresholds, [key]: val } });

  const removeThreshold = (key: string) => {
    const { [key]: _, ...rest } = item.thresholds;
    onChange({ ...item, thresholds: rest });
  };

  return (
    <div className="border rounded-lg bg-surface-alt">
      <div className="flex items-center gap-2 px-3 py-2">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={expanded ? 'Collapse criterion' : 'Expand criterion'}
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </Button>
        <Badge variant="info">criterion</Badge>
        <span className="text-xs text-muted font-mono flex-1 truncate">{item.evaluatorKey || `#${index + 1}`}</span>
        <Button variant="ghost" size="sm" iconOnly aria-label="Remove criterion" onClick={onRemove}>
          <Trash2 size={12} />
        </Button>
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t">
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-xs text-muted mb-1">Evaluator key *</label>
              <Input
                placeholder="e.g. strategy.walk_forward"
                value={item.evaluatorKey}
                onChange={(e) => setField('evaluatorKey', e.target.value)}
                className="text-xs py-1"
              />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Modality</label>
              <Input
                placeholder="optional run mode"
                value={item.modality ?? ''}
                onChange={(e) => setField('modality', e.target.value || undefined)}
                className="text-xs py-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-muted mb-1">Min trials</label>
              <Input
                type="number"
                min={0}
                value={String(item.minTrials)}
                onChange={(e) => setField('minTrials', parseInt(e.target.value, 10) || 0)}
                className="text-xs py-1"
              />
            </div>
            <div className="flex items-end pb-1">
              <Toggle size="sm" label="Required" checked={item.required} onChange={(v) => setField('required', v)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-muted">Thresholds</label>
              <Button variant="ghost" size="sm" onClick={addThreshold}>
                <Plus size={12} /> Add
              </Button>
            </div>
            {Object.entries(item.thresholds).map(([key, val], ti) => (
              <ThresholdRow
                key={`threshold-${ti}`}
                metricKey={key}
                value={val}
                onChangeKey={(nk) => updateThresholdKey(key, nk)}
                onChangeValue={(nv) => updateThresholdValue(key, nv)}
                onRemove={() => removeThreshold(key)}
              />
            ))}
            {Object.keys(item.thresholds).length === 0 && (
              <p className="text-xs text-faint">No thresholds — evaluator runs but output not checked.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Template ref editor ───────────────────────────────────────────────────────

interface TemplateRefEditorProps {
  item: TemplateRefItem;
  onChange: (updated: TemplateRefItem) => void;
  onRemove: () => void;
}

const TemplateRefEditor: React.FC<TemplateRefEditorProps> = ({ item, onChange, onRemove }) => {
  const setField = <K extends keyof TemplateRefItem>(key: K, val: TemplateRefItem[K]) =>
    onChange({ ...item, [key]: val });

  return (
    <div className="border rounded-lg bg-surface-alt px-3 py-3">
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="neutral">template_ref</Badge>
        <span className="text-xs text-muted flex-1 truncate">{item.childTemplateRef || 'unnamed ref'}</span>
        <Button variant="ghost" size="sm" iconOnly aria-label="Remove template ref" onClick={onRemove}>
          <Trash2 size={12} />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-muted mb-1">Child template ref *</label>
          <Input
            placeholder="e.g. base-strategy-checks"
            value={item.childTemplateRef}
            onChange={(e) => setField('childTemplateRef', e.target.value)}
            className="text-xs py-1"
          />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Pinned version *</label>
          <Input
            type="number"
            min={1}
            value={String(item.childVersion)}
            onChange={(e) => setField('childVersion', parseInt(e.target.value, 10) || 1)}
            className="text-xs py-1"
          />
        </div>
      </div>
      <div className="mt-2">
        <Toggle size="sm" label="Required" checked={item.required} onChange={(v) => setField('required', v)} />
      </div>
    </div>
  );
};

// ── Main composer ─────────────────────────────────────────────────────────────

export const SpecComposer: React.FC<SpecComposerProps> = ({ items, onChange }) => {
  const updateItem = (index: number, updated: SpecItem) => {
    const next = items.slice();
    next[index] = updated;
    onChange(next);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {items.map((item, i) =>
        item.kind === 'criterion' ? (
          <CriterionEditor
            key={`item-${i}`}
            item={item}
            index={i}
            onChange={(u) => updateItem(i, u)}
            onRemove={() => removeItem(i)}
          />
        ) : (
          <TemplateRefEditor
            key={`item-${i}`}
            item={item}
            onChange={(u) => updateItem(i, u)}
            onRemove={() => removeItem(i)}
          />
        ),
      )}

      {items.length === 0 && (
        <p className="text-xs text-faint py-2">No items yet — add a criterion or template ref below.</p>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={() => onChange([...items, newCriterion()])}>
          <Plus size={13} /> Add criterion
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onChange([...items, newTemplateRef()])}>
          <Plus size={13} /> Add template ref
        </Button>
      </div>
    </div>
  );
};
